import { GRID_CONFIG } from "../config/grid";
import type { GridCell, PlacedObject } from "../config/types";

// --- Type Definitions ---
export interface RoadSegment {
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  type: "small" | "big" | "bridge";
  width: number;
}

export interface RoadCluster {
  id: string;
  buildings: GridCell[];
  centroid: GridCell;
  hub: GridCell | null;
  isCore: boolean;
}

export interface RoadNetwork {
  clusters: RoadCluster[];
  smallRoads: RoadSegment[];
  bigRoads: RoadSegment[];
  allRoadSegments: RoadSegment[];
}

// --- Configuration ---
const CONFIG = {
  SMALL_ROAD_WIDTH: 0.5,
  BRIDGE_ROAD_WIDTH: 0.8,
};

// --- Helper Functions ---

function cellKey(x: number, z: number): string {
  return `${x},${z}`;
}

function vertexKey(x: number, z: number): string {
  return `${x.toFixed(2)},${z.toFixed(2)}`;
}

function parseVertexKey(key: string): [number, number] {
  const [x, z] = key.split(",").map(Number);
  return [x, z];
}

function getSegmentKey(s: RoadSegment): string {
  const x1 = Math.min(s.x1, s.x2);
  const x2 = Math.max(s.x1, s.x2);
  const z1 = Math.min(s.z1, s.z2);
  const z2 = Math.max(s.z1, s.z2);
  return `${x1.toFixed(2)},${z1.toFixed(2)}-${x2.toFixed(2)},${z2.toFixed(2)}`;
}

// --- Union-Find for Buildings ---
class BuildingUnionFind {
  private parent: number[];
  private rank: number[];

  constructor(size: number) {
    this.parent = Array.from({ length: size }, (_, i) => i);
    this.rank = Array(size).fill(0);
  }

  find(x: number): number {
    if (this.parent[x] !== x) {
      this.parent[x] = this.find(this.parent[x]);
    }
    return this.parent[x];
  }

  union(x: number, y: number): void {
    const rx = this.find(x);
    const ry = this.find(y);
    if (rx === ry) return;
    if (this.rank[rx] < this.rank[ry]) this.parent[rx] = ry;
    else if (this.rank[rx] > this.rank[ry]) this.parent[ry] = rx;
    else { this.parent[ry] = rx; this.rank[rx]++; }
  }

  connected(x: number, y: number): boolean {
    return this.find(x) === this.find(y);
  }
}

// --- Main Algorithm ---

export function calculateRoadNetwork(buildings: PlacedObject[]): RoadNetwork {
  console.log("[RoadNetwork] Starting for", buildings.length, "buildings");
  
  if (buildings.length === 0) {
    return { clusters: [], smallRoads: [], bigRoads: [], allRoadSegments: [] };
  }

  const cellSize = GRID_CONFIG.CELL_SIZE;
  const allSegments = new Map<string, RoadSegment>();
  
  // === Phase 1: Build cell -> building index map ===
  const cellToBuilding = new Map<string, number>();
  const buildingCells: GridCell[] = [];
  
  for (let i = 0; i < buildings.length; i++) {
    const pos = buildings[i].position;
    const cellX = Math.floor(pos[0] / cellSize);
    const cellZ = Math.floor(pos[2] / cellSize);
    cellToBuilding.set(cellKey(cellX, cellZ), i);
    buildingCells.push({ x: cellX, z: cellZ });
  }

  // === Phase 2: Generate ONLY shared edges between adjacent buildings ===
  // For each building, check 4 neighbors. Only create edge if neighbor exists.
  const directions = [
    { dx: 0, dz: -1, edge: "top" },    // top neighbor -> shared top edge
    { dx: 0, dz: 1, edge: "bottom" },  // bottom neighbor -> shared bottom edge
    { dx: -1, dz: 0, edge: "left" },   // left neighbor -> shared left edge
    { dx: 1, dz: 0, edge: "right" },   // right neighbor -> shared right edge
  ];

  const uf = new BuildingUnionFind(buildings.length);
  let sharedEdgeCount = 0;

  // Track which neighbors each cell has
  const cellNeighbors = new Map<string, { top: boolean; bottom: boolean; left: boolean; right: boolean }>();

  for (let i = 0; i < buildingCells.length; i++) {
    const cell = buildingCells[i];
    const x = cell.x * cellSize;
    const z = cell.z * cellSize;
    const key = cellKey(cell.x, cell.z);
    
    const neighbors = { top: false, bottom: false, left: false, right: false };

    for (const dir of directions) {
      const neighborKey = cellKey(cell.x + dir.dx, cell.z + dir.dz);
      const neighborIdx = cellToBuilding.get(neighborKey);
      
      if (neighborIdx !== undefined) {
        neighbors[dir.edge as keyof typeof neighbors] = true;
        
        // Only process edge once (smaller index creates edge)
        if (i < neighborIdx) {
          let edge: RoadSegment;
          
          if (dir.edge === "top") {
            edge = { x1: x, z1: z, x2: x + cellSize, z2: z, type: "small", width: CONFIG.SMALL_ROAD_WIDTH };
          } else if (dir.edge === "bottom") {
            edge = { x1: x, z1: z + cellSize, x2: x + cellSize, z2: z + cellSize, type: "small", width: CONFIG.SMALL_ROAD_WIDTH };
          } else if (dir.edge === "left") {
            edge = { x1: x, z1: z, x2: x, z2: z + cellSize, type: "small", width: CONFIG.SMALL_ROAD_WIDTH };
          } else { // right
            edge = { x1: x + cellSize, z1: z, x2: x + cellSize, z2: z + cellSize, type: "small", width: CONFIG.SMALL_ROAD_WIDTH };
          }
          
          const segKey = getSegmentKey(edge);
          if (!allSegments.has(segKey)) {
            allSegments.set(segKey, edge);
            sharedEdgeCount++;
          }
        }
        
        // Union adjacent buildings
        uf.union(i, neighborIdx);
      }
    }
    
    cellNeighbors.set(key, neighbors);
  }

  // Phase 2.1: Add internal connecting edges for cells with opposite neighbors
  let internalEdgeCount = 0;
  for (let i = 0; i < buildingCells.length; i++) {
    const cell = buildingCells[i];
    const x = cell.x * cellSize;
    const z = cell.z * cellSize;
    const neighbors = cellNeighbors.get(cellKey(cell.x, cell.z))!;

    // If has left AND right neighbors, add top or bottom internal edge to connect them
    if (neighbors.left && neighbors.right) {
      // Add top edge to connect left shared edge to right shared edge
      const edge: RoadSegment = {
        x1: x, z1: z, x2: x + cellSize, z2: z,
        type: "small", width: CONFIG.SMALL_ROAD_WIDTH,
      };
      const segKey = getSegmentKey(edge);
      if (!allSegments.has(segKey)) {
        allSegments.set(segKey, edge);
        internalEdgeCount++;
      }
    }

    // If has top AND bottom neighbors, add left or right internal edge to connect them
    if (neighbors.top && neighbors.bottom) {
      // Add left edge to connect top shared edge to bottom shared edge
      const edge: RoadSegment = {
        x1: x, z1: z, x2: x, z2: z + cellSize,
        type: "small", width: CONFIG.SMALL_ROAD_WIDTH,
      };
      const segKey = getSegmentKey(edge);
      if (!allSegments.has(segKey)) {
        allSegments.set(segKey, edge);
        internalEdgeCount++;
      }
    }
  }
  console.log("[RoadNetwork] Phase 2: Generated", sharedEdgeCount, "shared +", internalEdgeCount, "internal edges");

  // === Phase 2.5: Add at least one perimeter edge per connected component ===
  // For each component, find boundary cells and add one external edge
  const componentToBuildings = new Map<number, number[]>();
  for (let i = 0; i < buildings.length; i++) {
    const root = uf.find(i);
    if (!componentToBuildings.has(root)) componentToBuildings.set(root, []);
    componentToBuildings.get(root)!.push(i);
  }

  let perimeterEdgeCount = 0;
  for (const [, bldgIndices] of componentToBuildings) {
    // Find all cells in this component
    const componentCells = bldgIndices.map(i => buildingCells[i]);
    
    // For each cell, check if any edge is on the perimeter (no neighbor)
    let addedPerimeterEdge = false;
    
    for (const cell of componentCells) {
      if (addedPerimeterEdge) break;
      
      const x = cell.x * cellSize;
      const z = cell.z * cellSize;
      
      // Check each direction for perimeter (no neighbor = perimeter)
      const perimeterDirections = [
        { dx: 0, dz: -1, edge: () => ({ x1: x, z1: z, x2: x + cellSize, z2: z }) }, // top
        { dx: 0, dz: 1, edge: () => ({ x1: x, z1: z + cellSize, x2: x + cellSize, z2: z + cellSize }) }, // bottom
        { dx: -1, dz: 0, edge: () => ({ x1: x, z1: z, x2: x, z2: z + cellSize }) }, // left
        { dx: 1, dz: 0, edge: () => ({ x1: x + cellSize, z1: z, x2: x + cellSize, z2: z + cellSize }) }, // right
      ];
      
      for (const dir of perimeterDirections) {
        const neighborKey = cellKey(cell.x + dir.dx, cell.z + dir.dz);
        if (!cellToBuilding.has(neighborKey)) {
          // This is a perimeter edge - add it
          const edgeCoords = dir.edge();
          const seg: RoadSegment = {
            ...edgeCoords,
            type: "small",
            width: CONFIG.SMALL_ROAD_WIDTH,
          };
          const key = getSegmentKey(seg);
          if (!allSegments.has(key)) {
            allSegments.set(key, seg);
            perimeterEdgeCount++;
            addedPerimeterEdge = true;
            break;
          }
        }
      }
    }
  }
  console.log("[RoadNetwork] Phase 2.5: Added", perimeterEdgeCount, "perimeter edges");

  // === Phase 3: Connect disconnected components with MST-like approach ===
  let bridgeCount = 0;
  let iterations = 0;
  const maxIterations = buildings.length;

  while (iterations++ < maxIterations) {
    // Find all components
    const componentToBuildings = new Map<number, number[]>();
    for (let i = 0; i < buildings.length; i++) {
      const root = uf.find(i);
      if (!componentToBuildings.has(root)) componentToBuildings.set(root, []);
      componentToBuildings.get(root)!.push(i);
    }

    if (componentToBuildings.size <= 1) break;

    // Find largest component
    let mainRoot = -1;
    let maxSize = 0;
    for (const [root, bldgs] of componentToBuildings) {
      if (bldgs.length > maxSize) { maxSize = bldgs.length; mainRoot = root; }
    }

    // Find closest building pair between any non-main component and main component
    let bestI = -1;
    let bestJ = -1;
    let bestDist = Infinity;

    for (const [root, bldgs] of componentToBuildings) {
      if (root === mainRoot) continue;

      for (const i of bldgs) {
        const cellI = buildingCells[i];
        for (const j of componentToBuildings.get(mainRoot)!) {
          const cellJ = buildingCells[j];
          const dist = Math.abs(cellI.x - cellJ.x) + Math.abs(cellI.z - cellJ.z);
          if (dist < bestDist) {
            bestDist = dist;
            bestI = i;
            bestJ = j;
          }
        }
      }
    }

    if (bestI === -1) break;

    // Create bridge: Connect closest VERTICES (not cell centers)
    const ci = buildingCells[bestI];
    const cj = buildingCells[bestJ];
    
    // Get vertices of each cell
    const verticesI = [
      { x: ci.x * cellSize, z: ci.z * cellSize },
      { x: (ci.x + 1) * cellSize, z: ci.z * cellSize },
      { x: ci.x * cellSize, z: (ci.z + 1) * cellSize },
      { x: (ci.x + 1) * cellSize, z: (ci.z + 1) * cellSize },
    ];
    const verticesJ = [
      { x: cj.x * cellSize, z: cj.z * cellSize },
      { x: (cj.x + 1) * cellSize, z: cj.z * cellSize },
      { x: cj.x * cellSize, z: (cj.z + 1) * cellSize },
      { x: (cj.x + 1) * cellSize, z: (cj.z + 1) * cellSize },
    ];

    // Find closest vertex pair
    let closestVI = verticesI[0];
    let closestVJ = verticesJ[0];
    let minVertexDist = Infinity;
    for (const vi of verticesI) {
      for (const vj of verticesJ) {
        const d = Math.abs(vi.x - vj.x) + Math.abs(vi.z - vj.z);
        if (d < minVertexDist) {
          minVertexDist = d;
          closestVI = vi;
          closestVJ = vj;
        }
      }
    }

    // Create L-shaped bridge using vertices (all as "small" type)
    const { x: x1, z: z1 } = closestVI;
    const { x: x2, z: z2 } = closestVJ;

    if (x1 !== x2) {
      const seg: RoadSegment = {
        x1, z1, x2, z2: z1,
        type: "small", width: CONFIG.SMALL_ROAD_WIDTH,
      };
      const key = getSegmentKey(seg);
      if (!allSegments.has(key)) { allSegments.set(key, seg); bridgeCount++; }
    }
    if (z1 !== z2) {
      const seg: RoadSegment = {
        x1: x2, z1, x2, z2,
        type: "small", width: CONFIG.SMALL_ROAD_WIDTH,
      };
      const key = getSegmentKey(seg);
      if (!allSegments.has(key)) { allSegments.set(key, seg); bridgeCount++; }
    }

    // Connect bridge endpoints to nearest cell edge vertex
    // For cell I: connect closestVI to other vertices of cell I that form edges
    for (const vi of verticesI) {
      if (vi.x === closestVI.x || vi.z === closestVI.z) {
        // Same row or same column = can form edge
        if (vi.x !== closestVI.x || vi.z !== closestVI.z) {
          const seg: RoadSegment = {
            x1: closestVI.x, z1: closestVI.z, x2: vi.x, z2: vi.z,
            type: "small", width: CONFIG.SMALL_ROAD_WIDTH,
          };
          const key = getSegmentKey(seg);
          if (!allSegments.has(key)) { allSegments.set(key, seg); }
        }
      }
    }
    // For cell J: connect closestVJ to other vertices
    for (const vj of verticesJ) {
      if (vj.x === closestVJ.x || vj.z === closestVJ.z) {
        if (vj.x !== closestVJ.x || vj.z !== closestVJ.z) {
          const seg: RoadSegment = {
            x1: closestVJ.x, z1: closestVJ.z, x2: vj.x, z2: vj.z,
            type: "small", width: CONFIG.SMALL_ROAD_WIDTH,
          };
          const key = getSegmentKey(seg);
          if (!allSegments.has(key)) { allSegments.set(key, seg); }
        }
      }
    }

    uf.union(bestI, bestJ);
  }
  console.log("[RoadNetwork] Phase 3: Created", bridgeCount, "bridge segments");

  // === Phase 5: Dead-End Reduction (limit to 1 dead-end) ===
  // Find all degree-1 vertices and connect all but one to eliminate dead-ends
  
  // Build vertex adjacency graph
  const vertexAdj = new Map<string, Set<string>>();
  for (const seg of allSegments.values()) {
    const v1 = vertexKey(seg.x1, seg.z1);
    const v2 = vertexKey(seg.x2, seg.z2);
    if (!vertexAdj.has(v1)) vertexAdj.set(v1, new Set());
    if (!vertexAdj.has(v2)) vertexAdj.set(v2, new Set());
    vertexAdj.get(v1)!.add(v2);
    vertexAdj.get(v2)!.add(v1);
  }

  // Find all dead-ends (degree == 1)
  const deadEnds: string[] = [];
  for (const [v, neighbors] of vertexAdj) {
    if (neighbors.size === 1) {
      deadEnds.push(v);
    }
  }

  // Keep only 1 dead-end, connect the rest to form loops
  let deadEndFixed = 0;
  if (deadEnds.length > 1) {
    // Skip first dead-end, connect the rest
    for (let i = 1; i < deadEnds.length; i++) {
      const deadEnd = deadEnds[i];
      const [dx, dz] = parseVertexKey(deadEnd);
      
      // Find nearest vertex that's not the current neighbor
      const currentNeighbor = Array.from(vertexAdj.get(deadEnd)!)[0];
      let nearestV = "";
      let nearestDist = Infinity;
      
      for (const [v] of vertexAdj) {
        if (v === deadEnd || v === currentNeighbor) continue;
        const [vx, vz] = parseVertexKey(v);
        const dist = Math.abs(dx - vx) + Math.abs(dz - vz);
        if (dist < nearestDist && dist <= cellSize * 3) { // Max 3 cells away
          nearestDist = dist;
          nearestV = v;
        }
      }
      
      if (nearestV !== "") {
        // Create L-shaped connection
        const [nx, nz] = parseVertexKey(nearestV);
        
        if (dx !== nx) {
          const seg: RoadSegment = {
            x1: dx, z1: dz, x2: nx, z2: dz,
            type: "small", width: CONFIG.SMALL_ROAD_WIDTH,
          };
          allSegments.set(getSegmentKey(seg), seg);
        }
        if (dz !== nz) {
          const seg: RoadSegment = {
            x1: nx, z1: dz, x2: nx, z2: nz,
            type: "small", width: CONFIG.SMALL_ROAD_WIDTH,
          };
          allSegments.set(getSegmentKey(seg), seg);
        }
        deadEndFixed++;
      }
    }
  }
  console.log("[RoadNetwork] Phase 5: Fixed", deadEndFixed, "dead-ends (kept 1, had", deadEnds.length, ")");

  // === Phase 6: Merge collinear segments ===
  const merged = smoothRoadNetwork(Array.from(allSegments.values()));
  console.log("[RoadNetwork] Phase 6: Merged to", merged.length, "segments (from", allSegments.size, ")");

  // Build result (all roads are "small" now)
  const smallRoads = merged;
  const bigRoads: RoadSegment[] = [];

  return {
    clusters: [],
    smallRoads,
    bigRoads,
    allRoadSegments: merged,
  };
}

// --- Segment Merging ---

export function smoothRoadNetwork(segments: RoadSegment[]): RoadSegment[] {
  if (segments.length < 2) return segments;

  // Separate horizontal and vertical
  const horizontal: RoadSegment[] = [];
  const vertical: RoadSegment[] = [];

  for (const seg of segments) {
    const isHoriz = Math.abs(seg.z1 - seg.z2) < 0.01;
    const isVert = Math.abs(seg.x1 - seg.x2) < 0.01;
    
    if (isHoriz) {
      horizontal.push({
        ...seg,
        x1: Math.min(seg.x1, seg.x2),
        x2: Math.max(seg.x1, seg.x2),
      });
    } else if (isVert) {
      vertical.push({
        ...seg,
        z1: Math.min(seg.z1, seg.z2),
        z2: Math.max(seg.z1, seg.z2),
      });
    } else {
      // Diagonal - keep as-is
      horizontal.push(seg);
    }
  }

  return [...mergeLinear(horizontal, true), ...mergeLinear(vertical, false)];
}

function mergeLinear(segments: RoadSegment[], isHorizontal: boolean): RoadSegment[] {
  if (segments.length === 0) return [];

  // Group by line (same z for horizontal, same x for vertical)
  const groups = new Map<string, RoadSegment[]>();
  
  for (const seg of segments) {
    const lineKey = isHorizontal 
      ? `${seg.z1.toFixed(2)}_${seg.type}` 
      : `${seg.x1.toFixed(2)}_${seg.type}`;
    
    if (!groups.has(lineKey)) groups.set(lineKey, []);
    groups.get(lineKey)!.push(seg);
  }

  const result: RoadSegment[] = [];

  for (const segs of groups.values()) {
    // Sort by start position
    segs.sort((a, b) => isHorizontal ? a.x1 - b.x1 : a.z1 - b.z1);

    let current = { ...segs[0] };

    for (let i = 1; i < segs.length; i++) {
      const next = segs[i];
      const currentEnd = isHorizontal ? current.x2 : current.z2;
      const nextStart = isHorizontal ? next.x1 : next.z1;

      // Check if adjacent or overlapping (with small tolerance)
      if (Math.abs(currentEnd - nextStart) < 0.1) {
        // Extend current segment
        if (isHorizontal) {
          current.x2 = Math.max(current.x2, next.x2);
        } else {
          current.z2 = Math.max(current.z2, next.z2);
        }
      } else {
        // Gap - save current and start new
        result.push(current);
        current = { ...next };
      }
    }
    result.push(current);
  }

  return result;
}

// --- Shader Format Conversion ---

export function roadSegmentsToShaderFormat(
  segments: RoadSegment[],
  maxSegments: number = 500
): { positions: Float32Array; widths: Float32Array; count: number } {
  const positions = new Float32Array(maxSegments * 4);
  const widths = new Float32Array(maxSegments);

  const count = Math.min(segments.length, maxSegments);

  segments.slice(0, count).forEach((segment, i) => {
    const offset = i * 4;
    positions[offset] = segment.x1;
    positions[offset + 1] = segment.z1;
    positions[offset + 2] = segment.x2;
    positions[offset + 3] = segment.z2;
    widths[i] = segment.width;
  });

  return { positions, widths, count };
}

// --- Debug Helpers ---

export function getRoadNetworkStats(network: RoadNetwork): {
  totalClusters: number;
  totalSmallRoads: number;
  totalBigRoads: number;
  avgClusterSize: number;
} {
  return {
    totalClusters: network.clusters.length,
    totalSmallRoads: network.smallRoads.length,
    totalBigRoads: network.bigRoads.length,
    avgClusterSize: 0,
  };
}
